import type { QueryClient} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { UndefinedInitialDataOptions } from '@tanstack/react-query/src/queryOptions';

import type {
  GetTokenSearchParamsDeprecated} from '@api/token';
import {
  getSqrTokenPrice,
  getToken,
  getTokenChart
} from '@api/token';

const tokenQueryKeys = {
  token: (tokenId: string, params: GetTokenSearchParamsDeprecated = {}) => [
    'token',
    tokenId,
    params,
  ],
  tokenChart: (tokenId: string | number, params: GetTokenSearchParamsDeprecated = {}) => [
    'tokenChart',
    tokenId,
    params,
  ],
  sqrTokenPrice: ['sqrTokenPrice'],
};

export const useTokenQuery = (
  params: {
    tokenId: string;
    searchParams?: GetTokenSearchParamsDeprecated;
  },
  options?: Pick<
    UndefinedInitialDataOptions<Awaited<ReturnType<typeof getToken>>, unknown, unknown, any>,
    'enabled' | 'retry'
  >,
) => {
  return useQuery({
    queryKey: tokenQueryKeys.token(params.tokenId, params.searchParams),
    queryFn: () =>
      getToken(params.tokenId, {
        searchParams: params.searchParams,
      }),
    ...options,
  });
};

/**
 * @deprecated
 * This method is deprecated
 */
export const useTokenChartQueryDeprecated = (
  params: {
    tokenId: string;
    searchParams?: GetTokenSearchParamsDeprecated;
  },
  options?: Pick<
    UndefinedInitialDataOptions<Awaited<ReturnType<typeof getTokenChart>>, unknown, unknown, any>,
    'enabled'
  >,
) => {
  const { tokenId, searchParams } = params;

  return useQuery({
    queryKey: tokenQueryKeys.tokenChart(tokenId, searchParams),
    queryFn: () =>
      getTokenChart(tokenId, {
        searchParams,
      }),
    ...options,
  });
};

export const prefetchTokenQuery = (
  clientQuery: QueryClient,
  tokenId: string,
  options?: Parameters<typeof getToken>[1],
) => {
  return clientQuery.prefetchQuery({
    queryKey: tokenQueryKeys.token(tokenId, options?.searchParams),
    queryFn: () => getToken(tokenId, options),
  });
};

export const useSqrTokenPriceQuery = (
  options?: Pick<
    UndefinedInitialDataOptions<
      Awaited<ReturnType<typeof getSqrTokenPrice>>,
      unknown,
      unknown,
      any
    >,
    'enabled' | 'refetchInterval'
  >,
) => {
  return useQuery({
    queryKey: tokenQueryKeys.sqrTokenPrice,
    queryFn: () => getSqrTokenPrice(),
    ...options,
  });
};

export const prefetchSqrTokenPriceQuery = (
  clientQuery: QueryClient,
  options?: Parameters<typeof getSqrTokenPrice>[0],
) => {
  return clientQuery.prefetchQuery({
    queryKey: tokenQueryKeys.sqrTokenPrice,
    queryFn: () => getSqrTokenPrice(options),
  });
};
